import { resolveRecruitmentPermissions } from "./recruitmentPermissions";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../../backend/firebaseConfig";

const APPLICATION_DETAIL_DOCUMENTS = Object.freeze([
  "Data",
  "Previous Addresses",
  "Next of Kin Details",
  "Professional Qualifications",
  "Life Assurance Companies",
  "Work History",
  "Company Directorships",
  "Accountant Details",
  "Business Conduct",
  "Fitness and Propriety",
  "Overall Financial Position",
  "Additional Information",
  "Unemployment Details",
  "Declaration",
]);

export const recruitmentPipelinePath = (adviserId) =>
  doc(db, "Recruitment", adviserId);

export const saveSharedRecruitmentUpdate = async (adviserId, update) => {
  if (!adviserId) throw new Error("An adviser ID is required.");

  const applicationPersonalDetailsRef = doc(
    db,
    "Advisers",
    adviserId,
    "Personal Details",
    "Data",
  );
  const pipelineRef = recruitmentPipelinePath(adviserId);
  const pipelineSnapshot = await getDoc(pipelineRef);
  const hasPermissions = update.status != null || update.permission != null;
  const status = hasPermissions ? resolveRecruitmentPermissions(null, update) : null;
  const normalizedUpdate = hasPermissions
    ? { ...update, status, permission: status.join(", ") }
    : update;
  const sharedData = { recruitmentUpdates: [normalizedUpdate] };
  const batch = writeBatch(db);
  const adviserDetails = {
    ...(typeof update.isAR === "boolean" ? { isAR: update.isAR } : {}),
    ...(hasPermissions ? { status } : {}),
  };
  if (Object.keys(adviserDetails).length) {
    batch.set(doc(db, "Advisers", adviserId), adviserDetails, { merge: true });
  }

  batch.set(applicationPersonalDetailsRef, sharedData, { merge: true });

  if (pipelineSnapshot.exists()) {
    batch.set(pipelineRef, { ...sharedData, ...adviserDetails }, { merge: true });
    batch.set(
      doc(pipelineRef, "Personal Details", "Data"),
      sharedData,
      { merge: true },
    );
  }

  await batch.commit();
};

export const duplicateApplicationToRecruitment = async (
  adviserId,
  applicationUid = adviserId,
) => {
  if (!adviserId) throw new Error("An adviser ID is required.");
  if (!applicationUid) throw new Error("An application UID is required.");

  const adviserRef = doc(db, "Advisers", adviserId);
  const personalDetailsRef = doc(
    db,
    "Advisers",
    applicationUid,
    "Personal Details",
    "Data",
  );
  const applicationRefs = APPLICATION_DETAIL_DOCUMENTS.map((documentName) =>
    doc(db, "Advisers", applicationUid, "Application Details", documentName),
  );

  const [adviserSnapshot, personalDetailsSnapshot, ...applicationSnapshots] =
    await Promise.all([
      getDoc(adviserRef),
      getDoc(personalDetailsRef),
      ...applicationRefs.map((applicationRef) => getDoc(applicationRef)),
    ]);

  if (!adviserSnapshot.exists()) {
    throw new Error("The source adviser application could not be found.");
  }

  const batch = writeBatch(db);
  const pipelineRef = recruitmentPipelinePath(adviserId);
  const existingPipelineSnapshot = await getDoc(pipelineRef);

  batch.set(
    pipelineRef,
    {
      ...adviserSnapshot.data(),
      sourceAdviserId: adviserId,
      sourceApplicationUid: applicationUid,
      sourcePath: `Advisers/${applicationUid}`,
      referencingStatus: "REFERENCING",
      referencingStartedAt:
        existingPipelineSnapshot.data()?.referencingStartedAt ||
        serverTimestamp(),
      applicationCopyUpdatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  if (personalDetailsSnapshot.exists()) {
    batch.set(
      doc(pipelineRef, "Personal Details", "Data"),
      personalDetailsSnapshot.data(),
    );
  }

  applicationSnapshots.forEach((snapshot, index) => {
    if (!snapshot.exists()) return;

    batch.set(
      doc(
        pipelineRef,
        "Application Details",
        APPLICATION_DETAIL_DOCUMENTS[index],
      ),
      snapshot.data(),
    );
  });

  await batch.commit();
};

export const saveReferenceConsentFormToRecruitment = async (
  adviserId,
  consentFormBlob,
) => {
  if (!adviserId) throw new Error("An adviser ID is required.");
  if (!consentFormBlob) throw new Error("The reference consent form is missing.");

  const fileName = "Reference Authorisation Form.pdf";
  const storagePath = `Recruitment/${adviserId}/${fileName}`;
  const storageReference = ref(storage, storagePath);

  await uploadBytes(storageReference, consentFormBlob, {
    contentType: "application/pdf",
  });

  const attachment = {
    name: fileName,
    size: consentFormBlob.size,
    type: "application/pdf",
    path: storagePath,
    url: await getDownloadURL(storageReference),
    uploadedAt: Timestamp.now(),
    source: "reference-consent-form",
  };

  await setDoc(
    recruitmentPipelinePath(adviserId),
    { referenceConsentAttachment: attachment },
    { merge: true },
  );

  return attachment;
};

export const startContractDrafting = async (adviserId) => {
  if (!adviserId) throw new Error("An adviser ID is required.");

  const pipelineRef = recruitmentPipelinePath(adviserId);
  const pipelineSnapshot = await getDoc(pipelineRef);

  if (!pipelineSnapshot.exists()) {
    throw new Error("Referencing must be started before drafting a contract.");
  }

  await setDoc(
    pipelineRef,
    {
      contractDraftingStatus: "DRAFTING",
      contractDraftingStartedAt:
        pipelineSnapshot.data()?.contractDraftingStartedAt ||
        serverTimestamp(),
      contractDraftingUpdatedAt: serverTimestamp(),
    },
    { merge: true },
  );
};

export const saveRecruitmentApplicantDetails = async (
  adviserId,
  applicantDetails,
) => {
  if (!adviserId) throw new Error("An adviser ID is required.");

  await setDoc(
    doc(
      db,
      "Recruitment",
      adviserId,
      "Personal Details",
      "Data",
    ),
    {
      ...applicantDetails,
      referenceDetailsUpdatedAt: Timestamp.now(),
    },
    { merge: true },
  );

  return applicantDetails;
};
